import type { LegacyMatchAliasResolution, LegacyMatchAliasSourceKind } from './api'
import type { MatchDetailLocale } from './types'
import { notFound, redirect } from '@tanstack/react-router'
import { buildFootballCoreLocalePath } from '~/lib/navigation/football-core'
import { getServerRequest } from '~/lib/server-request'
import { fetchLegacyMatchAlias } from './api'
import { validateLegacyRedirectSearch } from './back-navigation'

interface LegacyRouteLocation {
  href: string
  pathname: string
  searchStr?: string
  hash?: string
}

export interface LegacyMatchAliasRouteState {
  kind: 'not_found' | 'collision'
  status: 404 | 409
  locale: MatchDetailLocale
}

export function isLegacyResearchMatchAliasPath(pathname: string): boolean {
  return /^(?:\/[^/]+)?\/research\/matches\/[^/]+\/?$/.test(pathname)
}

export async function loadLegacyMatchAliasRoute(input: {
  sourceKind: LegacyMatchAliasSourceKind
  sourceId: string
  locale: MatchDetailLocale
  location: LegacyRouteLocation
  rawUrl?: string
  resolve?: (
    sourceKind: LegacyMatchAliasSourceKind,
    sourceId: string,
  ) => Promise<LegacyMatchAliasResolution>
}): Promise<LegacyMatchAliasRouteState> {
  const rawUrl = input.rawUrl
    ?? getServerRequest()?.url
    ?? (typeof window === 'undefined' ? input.location.href : window.location.href)
  const sourceUrl = new URL(rawUrl, 'http://ballpeek.local')
  const searchValidation = validateLegacyRedirectSearch(sourceUrl.search, input.locale)
  if (!searchValidation.ok) {
    throw notFound({
      data: { kind: 'not_found', status: 404, locale: input.locale },
    })
  }

  const resolution = await (input.resolve ?? fetchLegacyMatchAlias)(input.sourceKind, input.sourceId)
  if (resolution.disposition === 'unresolved') {
    throw notFound({
      data: { kind: 'not_found', status: 404, locale: input.locale },
    })
  }
  if (resolution.disposition === 'collision')
    return { kind: 'collision', status: 409, locale: input.locale }

  const search = searchValidation.search
  const hash = sourceUrl.hash
  const targetPath = resolution.disposition === 'canonical'
    ? `/matches/${encodeURIComponent(resolution.matchId)}`
    : `/history/matches/${encodeURIComponent(resolution.researchMatchId)}`
  const statusCode = resolution.disposition === 'canonical' && resolution.stability === 'terminal'
    ? 308
    : 307

  throw redirect({
    href: buildFootballCoreLocalePath(input.locale, targetPath, search, hash),
    statusCode,
    replace: true,
  })
}
